import React, { useState, useMemo, useCallback } from 'react';

const LANGS = [
    { id: 'python', label: 'Python' },
    { id: 'curl', label: 'cURL' },
    { id: 'postman', label: 'Postman' },
];

function paramsToFormData(params, lang) {
    const entries = Object.entries(params).filter(([, v]) => v !== null && v !== undefined);
    if (lang === 'python') {
        return entries.map(([k, v]) => {
            const val = typeof v === 'boolean' ? (v ? 'True' : 'False') : typeof v === 'string' ? `"${v}"` : v;
            return `    "${k}": ${val},`;
        }).join('\n');
    }
    if (lang === 'curl') {
        return entries.map(([k, v]) => `    -F "${k}=${v}"`).join(' \\\n');
    }
    return entries;
}

function generateSteps(params, apiUrl, apiKey, lang) {
    const formData = paramsToFormData(params, lang);

    if (lang === 'python') {
        return [
            {
                title: 'Cài đặt thư viện',
                desc: 'Cài đặt thư viện requests nếu chưa có.',
                code: `pip install requests`,
            },
            {
                title: 'Tạo task OCR',
                desc: 'Upload file PDF hoặc ảnh để tạo task OCR. API trả về taskId để theo dõi.',
                code: `import requests

API_URL = "${apiUrl}"
API_KEY = "${apiKey}"
HEADERS = {"Authorization": f"Bearer {API_KEY}"}

with open("document.pdf", "rb") as f:
    resp = requests.post(
        f"{API_URL}/tasks/create",
        headers=HEADERS,
        files={"file": ("document.pdf", f, "application/pdf")},
        data={
${formData}
        },
    )

result = resp.json()
task_id = result["result"]["taskId"]
print(f"Task created: {task_id}")`,
            },
            {
                title: 'Theo dõi trạng thái',
                desc: 'Gọi API lấy chi tiết task mỗi 3 giây cho đến khi hoàn thành hoặc thất bại.',
                code: `import time

while True:
    resp = requests.get(f"{API_URL}/tasks/{task_id}/detail", headers=HEADERS)
    detail = resp.json()["result"]
    status = detail["statusText"]
    pages = f'{detail["extractedPages"]}/{detail["totalPages"]}'
    print(f"Status: {status} | Pages: {pages}")

    if status == "completed":
        print(f"Done in {detail['inferenceTime']}s")
        break
    elif status == "failed":
        print(f"Error: {detail.get('error')}")
        break

    time.sleep(3)`,
            },
            {
                title: 'Lấy kết quả',
                desc: 'Sau khi hoàn thành, lấy kết quả phân tích dạng JSON hoặc markdown.',
                code: `# Cách 1: Lấy kết quả chi tiết (JSON gồm pages, blocks, layout)
resp = requests.get(f"{API_URL}/tasks/{task_id}/detail", headers=HEADERS)
pages = resp.json()["result"]["parsingResult"]
for page in pages:
    print(f"--- Trang {page['pageIndex'] + 1} ---")
    print(page["markdown"])

# Cách 2: Lấy toàn bộ markdown
resp = requests.get(f"{API_URL}/tasks/{task_id}/markdown", headers=HEADERS)
print(resp.text)`,
            },
        ];
    }

    if (lang === 'curl') {
        return [
            {
                title: 'Tạo task OCR',
                desc: 'Upload file và nhận taskId.',
                code: `curl -X POST "${apiUrl}/tasks/create" \\
    -H "Authorization: Bearer ${apiKey}" \\
    -F "file=@document.pdf" \\
${formData}`,
            },
            {
                title: 'Theo dõi trạng thái',
                desc: 'Thay TASK_ID bằng taskId từ bước 1. Lặp lại cho đến khi statusText là "completed".',
                code: `TASK_ID="t-xxxxxxxxxxxx"

# Single check
curl -s "${apiUrl}/tasks/\${TASK_ID}/detail" \\
    -H "Authorization: Bearer ${apiKey}" | python3 -m json.tool

# Auto-polling loop
while true; do
    RESULT=$(curl -s "${apiUrl}/tasks/\${TASK_ID}/detail" \\
        -H "Authorization: Bearer ${apiKey}")
    echo "$RESULT" | python3 -c "
import sys,json; d=json.load(sys.stdin)['result']
print(f'Status: {d[\"statusText\"]} | Pages: {d[\"extractedPages\"]}/{d[\"totalPages\"]}')
"
    STATUS=$(echo "$RESULT" | python3 -c "import sys,json; print(json.load(sys.stdin)['result']['statusText'])")
    [ "$STATUS" = "completed" ] || [ "$STATUS" = "failed" ] && break
    sleep 3
done`,
            },
            {
                title: 'Lấy kết quả markdown',
                desc: 'Lấy toàn bộ nội dung markdown của task đã hoàn thành.',
                code: `curl -s "${apiUrl}/tasks/\${TASK_ID}/markdown" \\
    -H "Authorization: Bearer ${apiKey}"`,
            },
        ];
    }

    if (lang === 'postman') {
        const entries = Object.entries(params).filter(([, v]) => v !== null && v !== undefined);
        const collection = {
            info: {
                name: "PaddleOCR API",
                schema: "https://schema.getpostman.com/json/collection/v2.1.0/collection.json",
            },
            variable: [
                { key: "api_url", value: apiUrl },
                { key: "api_key", value: apiKey },
                { key: "task_id", value: "t-xxxxxxxxxxxx" },
            ],
            item: [
                {
                    name: "1. Create Task",
                    request: {
                        method: "POST",
                        url: "{{api_url}}/tasks/create",
                        header: [{ key: "Authorization", value: "Bearer {{api_key}}" }],
                        body: {
                            mode: "formdata",
                            formdata: [
                                { key: "file", type: "file", src: "document.pdf" },
                                ...entries.map(([k, v]) => ({ key: k, value: String(v), type: "text" })),
                            ],
                        },
                    },
                },
                {
                    name: "2. Poll Task Detail",
                    request: {
                        method: "GET",
                        url: "{{api_url}}/tasks/{{task_id}}/detail",
                        header: [{ key: "Authorization", value: "Bearer {{api_key}}" }],
                    },
                },
                {
                    name: "3. Get Markdown",
                    request: {
                        method: "GET",
                        url: "{{api_url}}/tasks/{{task_id}}/markdown",
                        header: [{ key: "Authorization", value: "Bearer {{api_key}}" }],
                    },
                },
            ],
        };
        return [
            {
                title: 'Import Postman Collection',
                desc: 'Tải và import collection này vào Postman. Cập nhật biến api_key bằng token của bạn.',
                code: JSON.stringify(collection, null, 2),
                isPostman: true,
            },
        ];
    }

    return [];
}

function CodeBlock({ code, onCopy, copied }) {
    return (
        <div className="relative group">
            <button
                onClick={onCopy}
                className="absolute top-2 right-2 px-2 py-1 text-[11px] rounded bg-gray-300 text-gray-600 opacity-0 group-hover:opacity-100 hover:bg-gray-400 transition-all"
            >
                {copied ? 'Copied!' : 'Copy'}
            </button>
            <pre className="p-4 text-[13px] leading-relaxed text-gray-800 whitespace-pre overflow-x-auto font-mono bg-gray-100 rounded-lg border border-gray-200">
                {code}
            </pre>
        </div>
    );
}

export default function OcrGetCode({ params }) {
    const [lang, setLang] = useState('python');
    const [copiedIdx, setCopiedIdx] = useState(-1);

    const apiUrl = 'http://34.143.252.12:9090';
    const apiKey = 'sk-paddleocr-simplize-2026';

    const steps = useMemo(() => generateSteps(params, apiUrl, apiKey, lang), [lang, params, apiUrl]);

    const handleCopy = useCallback((code, idx) => {
        navigator.clipboard.writeText(code);
        setCopiedIdx(idx);
        setTimeout(() => setCopiedIdx(-1), 2000);
    }, []);

    const handleCopyAll = useCallback(() => {
        const all = steps.map(s => s.code).join('\n\n');
        navigator.clipboard.writeText(all);
        setCopiedIdx(99);
        setTimeout(() => setCopiedIdx(-1), 2000);
    }, [steps]);

    const handleDownloadPostman = useCallback(() => {
        const code = steps[0]?.code;
        if (!code) return;
        const blob = new Blob([code], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'paddleocr-api.postman_collection.json';
        a.click();
        URL.revokeObjectURL(url);
    }, [steps]);

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-center gap-2 mb-4">
                {LANGS.map(l => (
                    <button
                        key={l.id}
                        onClick={() => setLang(l.id)}
                        className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                            lang === l.id
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                        {l.label}
                    </button>
                ))}
                <div className="flex-1" />
                <button
                    onClick={handleCopyAll}
                    className="px-3 py-1.5 text-sm rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 font-medium transition-colors"
                >
                    {copiedIdx === 99 ? 'Copied!' : 'Copy t\u1EA5t c\u1EA3'}
                </button>
                {lang === 'postman' && (
                    <button
                        onClick={handleDownloadPostman}
                        className="px-3 py-1.5 text-sm rounded-md bg-orange-100 text-orange-700 hover:bg-orange-200 font-medium transition-colors"
                    >
                        T\u1EA3i Collection
                    </button>
                )}
            </div>

            <div className="flex-1 overflow-y-auto space-y-5">
                {steps.map((step, i) => (
                    <div key={i}>
                        <div className="flex items-baseline gap-2 mb-1">
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-[11px] font-bold flex-shrink-0">
                                {i + 1}
                            </span>
                            <h4 className="text-sm font-semibold text-gray-800">{step.title}</h4>
                        </div>
                        <p className="text-[12px] text-gray-500 mb-2 ml-7">{step.desc}</p>
                        <div className="ml-7">
                            <CodeBlock
                                code={step.code}
                                copied={copiedIdx === i}
                                onCopy={() => handleCopy(step.code, i)}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
